
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import BaseFilter from './BaseFilter.vue';
import { ValueText } from '../../../../store/models.def';

interface CategoryData {
	value: string[];
	text: string;
	selected: boolean;
	indeterminate: boolean;
}

@Component({
	components: {},
})
export default class ListFilter extends BaseFilter {
    public get processedOptions(): ValueText[] {
        if (this.showCode) {
            return this.options.map((opt) => ({
                ...opt,
                text: opt.value,  // Replace text with value (the code)
            }));
        }
        return this.options;
    }
	public get limitedOptions() {
		return this.filteredOptions.slice(0, this.optionsLimit);
	}
	public get limitedCategories() {
		return this.filteredCategories.slice(0, this.categoriesLimit);
	}

	public get filteredCategories(): CategoryData[] {
		if (!this.searchString || !this.searchable) {
			return this.categoryDatas;
		}
		const keywords = this.searchString
			.toLowerCase()
			.split(/,\s*/g)
			.map((s) => s.trim())
			.filter((s) => s);

		return this.categoryDatas.filter((data) => this.match(keywords, data.text));
	}
	public get filteredOptions(): ValueText[] {
		if (!this.searchString
            || !this.searchable
        ) {
			return this.processedOptions;
		}
		const keywords = this.searchString
			.toLowerCase()
			.split(/,\s*/g)
			.map((s) => s.trim())
			.filter((s) => s);
		this.optionsLimit = 10;
		this.categoriesLimit = 10;
		return this.processedOptions.filter(
			(data) =>
				this.match(keywords, data.text) || this.match(keywords, data.value),
		);
	}

	/** True while the user has typed something into the search box. */
	public get searchActive(): boolean {
		return !!this.searchable && !!this.searchString.trim();
	}

	/** Values of every option matching the search (not just the rendered ones). */
	public get filteredValues(): string[] {
		return this.filteredOptions.map((vt) => vt.value);
	}

	public get filteredAllSelected(): boolean {
		const values = this.filteredValues;
		return values.length > 0 && values.every((v) => this.selected.indexOf(v) !== -1);
	}

	public get filteredSomeSelected(): boolean {
		return this.filteredValues.some((v) => this.selected.indexOf(v) !== -1);
	}

	@Prop({ default: 'List Name' }) public filterName!: string;
	@Prop({ default: 'fa fa-list' }) public iconClass!: string;
	@Prop({ default: () => [] }) public options!: ValueText[];
	@Prop({ default: () => [] }) public categories!: ValueText[];
	@Prop({ default: () => [] }) public value!: string[];
	@Prop({ default: true }) public searchable!: boolean;
    @Prop({ default: false }) public activity?: boolean;
    @Prop({ default: false }) public showCode?: boolean;

    public debounceTimer: any = null;
	public optionsLimit = 10;
	public categoriesLimit = 10;

	public selected: string[] = this.value;
	// public selectedCategories: string[] = this.value;
	public categoryDatas: CategoryData[] = [];
	// public selected: string[] = [];
	public allSelected = false;
	public indeterminate = false;
    public includeNonactive = true;

	public searchString: string = '';
	public onLoadMoreOptions() {
		this.optionsLimit += 10;
	}
	public onLoadMoreCategories() {
		this.categoriesLimit += 10;
	}

    @Watch('includeNonactive')
    public onIncludeNonactiveChange() {
        this.$emit('nonactive-toggle', this.includeNonactive);
    }

    @Watch('searchString')
    public onSearchStringChange() {
        // Use debounce to delay filtering
        this.debounce(() => {
            // Code to update limitedOptions
            this.optionsLimit = 10; // Reset options limit
            this.categoriesLimit = 10; // Reset categories limit
        }, 300); // 300ms delay
    }

	@Watch('categories', { immediate: true })
	public onCategoriesChange(newVal: ValueText[], oldVal: ValueText[]) {
		this.categoryDatas = newVal.map((vt) => ({
			value: vt.value,
			text: vt.text,
			selected: false,
			indeterminate: false,
		}));
		this.updateCategoryDatas();
	}

    public toggleInactive(checked: boolean) {
        this.selected = this.processedOptions.map((vt) => vt.value).sort();
    }

	public toggleAll(checked: boolean) {
		this.selected = (checked ? this.processedOptions.map((vt) => vt.value) : []).sort();
	}

	/**
	 * Add (or remove) every option matching the current search, keeping the rest
	 * of the selection intact — so successive searches accumulate.
	 */
	public toggleFiltered(checked: boolean) {
		const matching = this.filteredValues;
		if (checked) {
			const set = new Set([...this.selected, ...matching]);
			this.selected = Array.from(set).sort();
		} else {
			this.selected = this.selected
				.filter((value) => matching.indexOf(value) === -1)
				.sort();
		}
	}
	@Watch('value')
	public onValueChange(newVal: string[], oldVal: string[]) {
		this.selected = newVal;
	}
	@Watch('selected')
	public onSelectedChange(newVal: string[], oldVal: string[]) {
		if (newVal.length === 0) {
			this.indeterminate = false;
			this.allSelected = false;
			this.categoryDatas.forEach((data) => {
				data.indeterminate = false;
				data.selected = false;
			});
		} else if (newVal.length === this.processedOptions.length) {
			this.indeterminate = false;
			this.allSelected = true;
			this.categoryDatas.forEach((data) => {
				data.indeterminate = false;
				data.selected = true;
			});
		} else {
			this.indeterminate = true;
			this.allSelected = false;
			this.updateCategoryDatas();
		}
		if (this.selected !== this.value) {
			this.$emit('input', this.selected);
		}
	}

	public updateCategoryDatas() {
		const datas = this.categoryDatas;
		const clone = [...this.selected];
		for (const category of datas) {
			const found = matchCategory(category.value, clone);
			if (found === 0) {
				category.selected = false;
				category.indeterminate = false;
			} else if (found === category.value.length) {
				category.selected = true;
				category.indeterminate = false;
			} else {
				category.selected = false;
				category.indeterminate = true;
			}
		}

		function matchCategory(members: string[], selected: string[]): number {
			let found = 0;
			for (const member of members) {
				const i = selected.indexOf(member);
				if (i !== -1) {
					selected.splice(i, 1);
					found++;
				}
			}
			return found;
		}
	}

    // Other properties and methods remain unchanged...

    // Method to debounce function calls
    public debounce(func: () => void, wait: number) {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(func, wait);
    }

	public match(keywords: string[], target: string) {
        const text = target.toString().toLowerCase();

        // If keywords is empty, no match should occur
        if (keywords.length === 0) {
            return false;
        }

        // Check if any keyword is present in the text
        for (const keyword of keywords) {
            if (text.indexOf(keyword) !== -1) {
                return true;
            }
        }

        // If no keyword matches, return false
        return false;
	}

	public selectCategory(data: CategoryData) {
		const members = data.value;
		// data.selected already reflects the NEW state (after v-model update)
		// so if selected=true, user wants to ADD items; if false, REMOVE items
		if (data.selected) {
			const set = new Set([...this.selected, ...members]);
			this.selected = Array.from(set).sort();
		} else {
			this.selected = this.selected
				.filter((value) => !members.includes(value))
				.sort();
		}
	}
}
